import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface AddressSuggestion {
  label: string
  address: string
  city: string
  state: string
  postal_code: string
  latitude: number
  longitude: number
}

/** Espelha o MIN_QUERY_LENGTH do AddressAutocompleteService. */
const MIN_QUERY_LENGTH = 3
/** Pausa na digitação que dispara a busca. Uma requisição por tecla seria absurdo. */
const DEBOUNCE_MS = 300

/**
 * Campo de endereço com sugestões enquanto se digita.
 *
 * Escolher uma opção preenche rua, CEP, cidade, UF **e** a coordenada de uma
 * vez — é isso que resolve o cadastro de endereço do wizard, em que a pessoa
 * digitava quatro campos soltos e depois pedia "Localizar no mapa" para
 * descobrir que tinha errado o CEP.
 *
 * Continua sendo um input comum: quem mora onde o OpenStreetMap não conhece
 * digita o endereço na mão e marca o ponto no mapa, como antes.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  invalid = false,
}: {
  value: string
  onChange: (value: string) => void
  /** Recebe a sugestão inteira — o formulário decide o que fazer com cada campo. */
  onSelect: (suggestion: AddressSuggestion) => void
  placeholder?: string
  invalid?: boolean
}) {
  const listId = useId()
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  /**
   * A busca só roda para o que foi DIGITADO. Sem esta trava, escolher uma
   * opção mudaria o `value` e dispararia uma busca nova pelo texto que acabou
   * de ser escolhido — a lista reabriria sozinha embaixo do cursor.
   */
  const skipNextSearch = useRef(false)

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false
      return
    }

    const query = value.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setOpen(false)
      return
    }

    // AbortController e não só um flag: resposta lenta de uma consulta antiga
    // chegando depois da nova sobrescreveria a lista com o resultado errado.
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `/enderecos?q=${encodeURIComponent(query)}`,
          {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        )
        if (!response.ok) return
        const body = await response.json()
        setSuggestions(body.results ?? [])
        setOpen(true)
        setHighlighted(-1)
      } catch {
        // Rede fora ou requisição cancelada: o campo segue digitável.
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [value])

  // Clique fora fecha. `mousedown` e não `click`: o clique numa opção da lista
  // precisa acontecer antes de qualquer fechamento por foco.
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const choose = (suggestion: AddressSuggestion) => {
    skipNextSearch.current = true
    onSelect(suggestion)
    setOpen(false)
    setSuggestions([])
    setHighlighted(-1)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!open || suggestions.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((i) => (i + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (event.key === 'Enter' && highlighted >= 0) {
      // Só engole o Enter quando há opção destacada — senão quem digitou o
      // endereço na mão não conseguiria submeter o formulário.
      event.preventDefault()
      choose(suggestions[highlighted])
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          highlighted >= 0 ? `${listId}-${highlighted}` : undefined
        }
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors',
          invalid
            ? 'border-red-300 focus:border-red-400'
            : 'border-gray-200 focus:border-gray-400',
        )}
      />

      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          buscando…
        </span>
      )}

      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.label} role="none">
              <button
                type="button"
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === highlighted}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => choose(suggestion)}
                className={cn(
                  'block w-full px-3 py-2 text-left text-sm transition-colors',
                  index === highlighted
                    ? 'bg-primary/5 text-gray-900'
                    : 'text-gray-700',
                )}
              >
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && suggestions.length === 0 && (
        <p className="absolute inset-x-0 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 shadow-lg">
          Nenhum endereço encontrado. Você pode digitar e marcar o ponto no
          mapa.
        </p>
      )}
    </div>
  )
}
